import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const THEMES = {
  dark: {
    bg: 'linear-gradient(160deg, #0b0f1c 0%, #05070d 55%, #020306 100%)',
    bg2: 'linear-gradient(160deg, #10141f 0%, #080a12 100%)',
    surface: 'linear-gradient(145deg, #161c2e 0%, #0d1119 100%)',
    surface2: 'linear-gradient(145deg, #1d2438 0%, #131829 100%)',
    border: 'rgba(148, 163, 184, 0.14)',
    borderHover: 'rgba(148, 163, 184, 0.4)',
    text: '#f5f7fa',
    textBody: '#cbd2e1',
    muted: '#7c8699',
    heading: '#f5f7fa',
    accent: '#8fa4f3',
    accentBg: '#8fa4f3',
    accentText: '#0a0e18',
  },
  light: {
    bg: 'linear-gradient(160deg, #f5f3fe 0%, #ece6fc 50%, #e0d9fa 100%)',
    bg2: 'linear-gradient(160deg, #ffffff 0%, #f3effc 100%)',
    surface: 'linear-gradient(145deg, #ffffff 0%, #f6f2ff 100%)',
    surface2: 'linear-gradient(145deg, #efe9fd 0%, #e3daf9 100%)',
    border: 'rgba(124, 58, 237, 0.15)',
    borderHover: 'rgba(124, 58, 237, 0.35)',
    text: '#241f3d',
    textBody: '#3f3a5c',
    muted: '#746e94',
    heading: '#1D4ED8',
    accent: '#6d4aff',
    accentBg: '#6d4aff',
    accentText: '#ffffff',
  },
};

// The site follows the device's light/dark setting by default. Flipping the
// switch overrides it for the current visit only (sessionStorage), so every
// new visit starts from the system setting again.
const OVERRIDE_KEY = 'dft-theme';
const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)');
const systemTheme = () => (darkQuery().matches ? 'dark' : 'light');

const readOverride = () => {
  try {
    const saved = sessionStorage.getItem(OVERRIDE_KEY);
    return saved === 'dark' || saved === 'light' ? saved : null;
  } catch {
    return null; // storage blocked (private mode etc.) — just follow the system
  }
};

const getInitialTheme = () => readOverride() || systemTheme();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  // Earlier versions saved the choice permanently in localStorage, which
  // stopped the site following the system setting; clear that once.
  useEffect(() => {
    try { localStorage.removeItem(OVERRIDE_KEY); } catch { /* storage blocked */ }
  }, []);

  const selectTheme = (newTheme) => {
    setTheme(newTheme);
    try { sessionStorage.setItem(OVERRIDE_KEY, newTheme); } catch { /* storage blocked */ }
  };

  const toggleTheme = () => selectTheme(theme === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Tell the browser too, so native controls (dropdown lists, scrollbars)
    // render in the matching light/dark style instead of the OS default.
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  // Track live system changes (e.g. OS switching to dark at sunset) unless
  // the visitor has flipped the switch this visit.
  useEffect(() => {
    const mediaQuery = darkQuery();
    const handleChange = (e) => {
      if (!readOverride()) setTheme(e.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const colors = THEMES[theme];

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, selectTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
